import { DestroyRef, effect, inject, Injectable, InjectionToken, type Signal } from '@angular/core';
import type { StringResolver } from '@pct-ts/typing';
import type { ActivatedRoute } from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, merge } from 'rxjs';
import { isNil } from '@pct-ts/typeguard';
import { TranslocoService } from '@jsverse/transloco';
import { Title } from '@angular/platform-browser';
import {
  isTranslateAbleText,
  type TranslateAbleText,
  translateTranslateAbleText,
} from '@pct-ng/i18n';
import { resolveString } from '@pct-ts/common';

/**
 * string[] = titles list
 */
export const ROUTE_TITLE_SEPARATOR = new InjectionToken<StringResolver<TranslateAbleText[]>>(
  'ROUTE_TITLE_SEPARATOR',
);

@Injectable({
  providedIn: 'root',
})
export class RouteTitleWatcherService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #titleService = inject(Title);
  readonly #translocoService = inject(TranslocoService);
  readonly #routeTitleSeparator =
    inject(ROUTE_TITLE_SEPARATOR, {
      optional: true,
    }) ??
    // Default
    ((titles: TranslateAbleText[]): string => titles.join(' > '));
  readonly #title = toSignal(
    merge(
      this.#router.events.pipe(filter(event => event instanceof NavigationEnd)),
      // Refresh when transloco load new scope
      this.#translocoService.events$.pipe(filter(({ type }) => type === 'translationLoadSuccess')),
    ).pipe(
      takeUntilDestroyed(this.#destroyRef),
      map(() => {
        // Extract routes
        const titles: TranslateAbleText[] = [];
        let route: ActivatedRoute | null | undefined = this.#router.routerState.root;

        route = this.#findActiveRoute(route);

        while (!isNil(route)) {
          const title: TranslateAbleText | undefined = route.snapshot.data['title'];
          if (isTranslateAbleText(title)) {
            titles.unshift(title);
          }

          const parenTitle = route.routeConfig?.title;
          if (isTranslateAbleText(parenTitle)) {
            titles.unshift(parenTitle);
          }

          route = route?.parent;
        }

        if (titles.length > 0) {
          return resolveString(
            this.#routeTitleSeparator,
            titles.map(title => translateTranslateAbleText(title, this.#translocoService)),
          );
        }
        console.error('No titles found in current route path');
        return '';
      }),
    ),
    { initialValue: '' },
  );
  get title(): Signal<string> {
    return this.#title;
  }

  constructor() {
    effect(() => this.#titleService.setTitle(this.#title()));
  }

  /**
   * Megkeresi az aktív route-ot a route fában
   */
  #findActiveRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }
}
